// src/v2/components/ViewControls.jsx
//
// VIEWCONTROLS-STANDARDIZE-S1 — shared right-zone toolbar group used by
// Offices, Leases, and Market Plan. Renders, in fixed order:
//   compact (before) → thin divider → view segmented control → +Add button
//
// COMPACT-SWITCH-BEFORE-TOGGLE — Compact/Comfortable density control renders
// to the LEFT of the view toggle, matching Expand-all which also renders to
// the LEFT. Position is determined by DENSITY_KIND_POSITION below.
// Plan view includes both Compact and Expand-all (Compact re-added as CSS-only density).
//
// Which (object, view) pairs show which density controls lives in one place
// (DENSITY_MANIFEST below) instead of being duplicated per page. When a
// view's manifest entry is empty, nothing renders for density — no
// placeholder, no divider, no reserved space — so the view toggle and +Add
// button never shift position.
//
// Callers keep owning their own density buttons (testids/styles/callbacks
// unchanged) and pass them in as render functions via `densityNodes`, keyed
// by DENSITY_KIND. This component only decides *which* kinds are visible
// for the current (object, view) and lays everything out in the fixed
// order. Each render function receives `isNarrow` so it can collapse to
// icon-only under narrow viewports, with the descriptive label still
// available via the button's own `title` tooltip.
//
// Props:
//   object        — "offices" | "leases" | "plan"
//   view          — current view-mode key for that object (must match a
//                   key in DENSITY_MANIFEST[object])
//   densityNodes  — { [DENSITY_KIND]: (isNarrow: boolean) => ReactNode }
//   viewToggle    — ReactNode — the view's segmented control (List/Tiles/Map,
//                   List/Timeline, Plan/What-If/Timeline, …)
//   addButton     — ReactNode | null — the primary "+Add" button, if any

import React, { useEffect, useState } from "react";
import { T } from "../../app/tokens.js";

export const DENSITY_KIND = {
  COMPACT_COMFORTABLE: "compactComfortable",
  EXPAND_COLLAPSE: "expandCollapse",
};

// Controls whether each density kind renders BEFORE or AFTER the view toggle.
//   "before" — renders to the left of the view toggle (e.g. Expand all, Compact)
//   "after"  — renders to the right of the view toggle (currently unused)
export const DENSITY_KIND_POSITION = {
  [DENSITY_KIND.COMPACT_COMFORTABLE]: "before",
  [DENSITY_KIND.EXPAND_COLLAPSE]:     "before",
};

// Single source of truth for which density controls each (object, view)
// pair supports. See VIEWCONTROLS-STANDARDIZE-S1/S2 task docs for the
// intent behind each entry.
//
// Offices Tiles is deliberately empty (VIEWCONTROLS-STANDARDIZE-S2):
// Tiles render fixed-size cards, so row density (Compact/Comfortable)
// doesn't apply there — it matches Map, which also has no density
// control.
export const DENSITY_MANIFEST = {
  offices: {
    list:  [DENSITY_KIND.COMPACT_COMFORTABLE],
    tiles: [],
    map:   [],
  },
  leases: {
    list:     [DENSITY_KIND.COMPACT_COMFORTABLE],
    timeline: [DENSITY_KIND.EXPAND_COLLAPSE],
  },
  plan: {
    plan:     [DENSITY_KIND.COMPACT_COMFORTABLE, DENSITY_KIND.EXPAND_COLLAPSE],
    whatif:   [],
    timeline: [],
  },
  projects: {
    list:  [DENSITY_KIND.COMPACT_COMFORTABLE],
    board: [],
  },
};

const NARROW_BREAKPOINT_PX = 820;

// Tracks whether the viewport is under the narrow breakpoint so density
// controls can collapse to icon-only. Uses matchMedia (not a resize
// listener on window.innerWidth) so it responds to viewport changes without
// polling, and degrades to `false` in environments without matchMedia
// (e.g. some test/SSR setups).
export function useIsNarrowToolbar(breakpoint = NARROW_BREAKPOINT_PX) {
  const [isNarrow, setIsNarrow] = useState(() => {
    try {
      return typeof window !== "undefined" && window.matchMedia
        ? window.matchMedia(`(max-width: ${breakpoint}px)`).matches
        : false;
    } catch (_) {
      return false;
    }
  });

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mql = window.matchMedia(`(max-width: ${breakpoint}px)`);
    const onChange = (e) => setIsNarrow(e.matches);
    setIsNarrow(mql.matches);
    if (mql.addEventListener) {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    // Safari <14 fallback
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [breakpoint]);

  return isNarrow;
}

export function ViewControls({ object, view, densityNodes = {}, viewToggle, addButton = null }) {
  const kinds = DENSITY_MANIFEST[object]?.[view] ?? [];
  const isNarrow = useIsNarrowToolbar();

  const beforeKinds = kinds.filter(k => (DENSITY_KIND_POSITION[k] ?? "before") === "before");
  const afterKinds  = kinds.filter(k => (DENSITY_KIND_POSITION[k] ?? "before") === "after");

  const renderKind = (kind) => {
    const render = densityNodes[kind];
    if (typeof render !== "function") return null;
    return <React.Fragment key={kind}>{render(isNarrow)}</React.Fragment>;
  };

  return (
    <div
      data-testid="view-controls"
      style={{ display: "inline-flex", alignItems: "center", gap: 8 }}
    >
      {beforeKinds.length > 0 && (
        <>
          <div data-testid="view-controls-density" style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            {beforeKinds.map(renderKind)}
          </div>
          <div
            aria-hidden="true"
            data-testid="view-controls-divider"
            style={{ width: 1, height: 22, background: "var(--v2-line)", flexShrink: 0 }}
          />
        </>
      )}
      {viewToggle}
      {afterKinds.length > 0 && (
        <>
          <div
            aria-hidden="true"
            style={{ width: 1, height: 22, background: "var(--v2-line)", flexShrink: 0 }}
          />
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
            {afterKinds.map(renderKind)}
          </div>
        </>
      )}
      {addButton}
    </div>
  );
}

// ── Switch-style density controls (TOGGLE-SWITCH-ROLLOUT) ──
//
// Two variants of the approved shadcn-style Switch primitive (label on right,
// blue track when on). Both share the same visual implementation so they
// render identically — only the label and prop names differ.
//
// Variant A — CompactSwitch: label "Compact". Off = comfortable rows (default).
//             On = dense/compact rows.
// Variant B — ExpandAllSwitch: label "Expand all". On = all rows expanded.
//             Off = all rows collapsed.
//
// The switch colors match the approved HeadcountTrendReport reference:
//   T.accent (blue) when on, T.switchOff (gray) when off.

export function SwitchControl({ checked, onToggle, label, testId }) {
  const trackStyle = {
    position:    "relative",
    width:       34,
    height:      20,
    borderRadius: 999,
    background:  checked ? T.accent : T.switchOff,
    transition:  "background 0.15s",
    flexShrink:  0,
    display:     "inline-block",
    verticalAlign: "middle",
    border:      "none",
    padding:     0,
    cursor:      "pointer",
  };
  const thumbStyle = {
    position:     "absolute",
    top:          2,
    left:         checked ? 16 : 2,
    width:        16,
    height:       16,
    borderRadius: "50%",
    background:   T.white,
    boxShadow:    "0 1px 2px rgba(16,36,58,0.3)",
    transition:   "left 0.15s",
    pointerEvents: "none",
  };
  const wrapStyle = {
    display:    "inline-flex",
    alignItems: "center",
    gap:        9,
    fontSize:   T.fsSm5,
    fontWeight: T.fwBold,
    color:      checked ? T.accent : T.mid,
    cursor:     "pointer",
    userSelect: "none",
    background: "none",
    border:     "none",
    padding:    0,
  };
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testId}
      onClick={onToggle}
      style={wrapStyle}
    >
      <span style={trackStyle} aria-hidden="true">
        <span style={thumbStyle} />
      </span>
      {label}
    </button>
  );
}

// Compact switch (Variant A) — pass `checked={density === "compact"}`.
export function CompactSwitch({ checked, onChange, testId = "compact-switch" }) {
  return (
    <SwitchControl
      checked={checked}
      onToggle={onChange}
      label="Compact"
      testId={testId}
    />
  );
}

// Expand-all switch (Variant B) — pass `expanded` from the parent's
// "all rows expanded" derived boolean.
export function ExpandAllSwitch({ expanded, onToggle, testId = "expand-all-switch" }) {
  return (
    <SwitchControl
      checked={expanded}
      onToggle={onToggle}
      label="Expand all"
      testId={testId}
    />
  );
}
